import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, History, LogIn, Activity, Download, FilterX } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDateTime = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const ACTION_COLORS = {
  created: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  imported: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  added: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  updated: 'bg-blue-50 text-blue-700 border-blue-200',
  saved: 'bg-blue-50 text-blue-700 border-blue-200',
  deleted: 'bg-red-50 text-red-700 border-red-200',
  removed: 'bg-red-50 text-red-700 border-red-200',
  ran: 'bg-violet-50 text-violet-700 border-violet-200',
  built: 'bg-violet-50 text-violet-700 border-violet-200',
  published: 'bg-violet-50 text-violet-700 border-violet-200',
};

const CustomerHistoryPage = () => {
  const { user, token } = useAuth();
  const [logons, setLogons] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMember, setFilterMember] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchData = useCallback(async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [logonsRes, actionsRes] = await Promise.all([
        axios.get(`${API}/history/my/logons`, { headers }),
        axios.get(`${API}/history/my/actions`, { headers }),
      ]);
      setLogons(logonsRes.data || []);
      setActions(actionsRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load history');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const members = useMemo(() => {
    const s = new Set();
    logons.forEach(r => r.username && s.add(r.username));
    actions.forEach(r => r.username && s.add(r.username));
    return [...s].sort();
  }, [logons, actions]);

  const actionTypes = useMemo(() => [...new Set(actions.map(r => r.action).filter(Boolean))].sort(), [actions]);

  const inDateRange = useCallback((ts) => {
    if (!ts) return false;
    const day = ts.slice(0, 10);
    if (dateFrom && day < dateFrom) return false;
    if (dateTo && day > dateTo) return false;
    return true;
  }, [dateFrom, dateTo]);

  const filteredLogons = useMemo(() =>
    logons.filter(r => (filterMember === 'all' || r.username === filterMember) && inDateRange(r.timestamp)),
    [logons, filterMember, inDateRange]);

  const filteredActions = useMemo(() =>
    actions.filter(r =>
      (filterMember === 'all' || r.username === filterMember) &&
      (filterAction === 'all' || r.action === filterAction) &&
      inDateRange(r.timestamp)),
    [actions, filterMember, filterAction, inDateRange]);

  const filtersActive = filterMember !== 'all' || filterAction !== 'all' || dateFrom || dateTo;
  const clearFilters = () => { setFilterMember('all'); setFilterAction('all'); setDateFrom(''); setDateTo(''); };

  const exportCsv = async (type) => {
    try {
      const res = await axios.get(`${API}/history/my/export/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(user?.company || 'company').replace(/\s+/g, '_')}_history_${type}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${type === 'logons' ? 'Logons' : 'Actions'} history exported`);
    } catch (err) {
      toast.error('Failed to export history');
    }
  };

  if (!user?.is_primary) {
    return (
      <Card><CardContent className="py-12 text-center text-slate-500" data-testid="history-restricted">
        <History className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <p>History is only available to your company's Primary user.</p>
      </CardContent></Card>
    );
  }

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>;

  return (
    <div className="space-y-6" data-testid="customer-history-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="customer-history-heading">History</h1>
        <p className="text-slate-600 mt-1">Activity for {user?.company}</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-3 px-4 flex flex-wrap items-end gap-3" data-testid="history-filters">
          <div className="space-y-1">
            <span className="text-xs font-medium text-slate-500">Team member</span>
            <Select value={filterMember} onValueChange={setFilterMember}>
              <SelectTrigger className="w-44 h-9" data-testid="filter-member">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All members</SelectItem>
                {members.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <span className="text-xs font-medium text-slate-500">Action type</span>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="w-40 h-9" data-testid="filter-action">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {actionTypes.map(a => <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <span className="text-xs font-medium text-slate-500">From</span>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 h-9" data-testid="filter-date-from" />
          </div>
          <div className="space-y-1">
            <span className="text-xs font-medium text-slate-500">To</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 h-9" data-testid="filter-date-to" />
          </div>
          {filtersActive && (
            <Button variant="ghost" size="sm" className="h-9 text-slate-500" onClick={clearFilters} data-testid="clear-filters-btn">
              <FilterX className="w-4 h-4 mr-1.5" />Clear filters
            </Button>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="logons">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <TabsList>
            <TabsTrigger value="logons" data-testid="logons-tab">
              <LogIn className="w-4 h-4 mr-2" />Logons ({filteredLogons.length}{filtersActive ? ` of ${logons.length}` : ''})
            </TabsTrigger>
            <TabsTrigger value="actions" data-testid="actions-tab">
              <Activity className="w-4 h-4 mr-2" />Actions ({filteredActions.length}{filtersActive ? ` of ${actions.length}` : ''})
            </TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => exportCsv('logons')} disabled={logons.length === 0} data-testid="export-logons-btn">
              <Download className="w-4 h-4 mr-2" />Export Logons CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportCsv('actions')} disabled={actions.length === 0} data-testid="export-actions-btn">
              <Download className="w-4 h-4 mr-2" />Export Actions CSV
            </Button>
          </div>
        </div>

        <TabsContent value="logons">
          <Card>
            {filteredLogons.length === 0 ? (
              <CardContent className="py-12 text-center text-slate-500" data-testid="logons-empty">
                <LogIn className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>{logons.length === 0 ? 'No logon records yet' : 'No logon records match the current filters'}</p>
              </CardContent>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="logons-table">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date / Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogons.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`logon-row-${i}`}>
                        <td className="p-3 text-sm font-medium text-slate-800">{r.username || '-'}</td>
                        <td className="p-3 text-sm text-slate-600">{r.email || '-'}</td>
                        <td className="p-3 text-sm text-slate-600">{formatDateTime(r.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="actions">
          <Card>
            {filteredActions.length === 0 ? (
              <CardContent className="py-12 text-center text-slate-500" data-testid="actions-empty">
                <Activity className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>{actions.length === 0 ? 'No actions recorded yet. Actions such as project changes, conversion runs, BREX validations, PDF publishes, Author saves and STE checks will appear here.' : 'No actions match the current filters'}</p>
              </CardContent>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="actions-table">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Item</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Project</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date / Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredActions.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`action-row-${i}`}>
                        <td className="p-3 text-sm font-medium text-slate-800 whitespace-nowrap">{r.username || '-'}</td>
                        <td className="p-3">
                          <Badge variant="outline" className={`text-xs capitalize ${ACTION_COLORS[r.action] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>{r.action}</Badge>
                        </td>
                        <td className="p-3 text-sm text-slate-700 whitespace-nowrap">{r.entity_type}</td>
                        <td className="p-3 text-sm text-slate-600 max-w-xs truncate" title={r.entity_name}>{r.entity_name || '-'}</td>
                        <td className="p-3 text-sm text-slate-600 whitespace-nowrap">{r.project || '-'}</td>
                        <td className="p-3 text-sm text-slate-600 whitespace-nowrap">{formatDateTime(r.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CustomerHistoryPage;
